/**
 * Centralized API client with caching and request deduplication
 * 
 * Features:
 * - Automatic caching with TTL
 * - Request deduplication (prevents duplicate simultaneous requests)
 * - Error handling
 * - Cache invalidation
 * 
 * @module utils/api/client
 */

import { API_HOST } from "../../constants";

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

class ApiClient {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private pendingRequests: Map<string, PendingRequest<any>> = new Map();
  private readonly defaultCacheTTL = 30 * 1000; // 30 seconds
  private readonly requestTimeout = 30 * 1000; // 30 seconds

  /**
   * Makes a GET request with caching and deduplication
   * 
   * @param endpoint - API endpoint (e.g., "/user/123")
   * @param options - Request options
   * @param options.cacheTTL - Cache time-to-live in milliseconds (default: 30000)
   * @param options.skipCache - Skip cache check and force fresh request
   * @returns Promise resolving to the response data
   * 
   * @example
   * ```ts
   * const user = await apiClient.get<User>("/user/123", {
   *   cacheTTL: 60000, // 1 minute
   * });
   * ```
   */
  async get<T>(endpoint: string, options?: {
    cacheTTL?: number;
    skipCache?: boolean;
  }): Promise<T> {
    const cacheKey = `GET:${endpoint}`;
    const cacheTTL = options?.cacheTTL ?? this.defaultCacheTTL;

    // Check cache first (unless skipCache is true)
    if (!options?.skipCache) {
      const cached = this.cache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < cacheTTL) {
        return cached.data;
      }
    }

    // Check if request is already pending
    const pending = this.pendingRequests.get(cacheKey);
    if (pending && (Date.now() - pending.timestamp) < this.requestTimeout) {
      return pending.promise;
    }

    // Create new request
    const promise = (async () => {
      try {
        const response = await fetch(`${API_HOST}${endpoint}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.statusText}`);
        }

        const data = await response.json();

        // Update cache
        this.cache.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });

        return data;
      } finally {
        // Remove from pending requests after completion
        this.pendingRequests.delete(cacheKey);
      }
    })();

    // Store pending request
    this.pendingRequests.set(cacheKey, {
      promise,
      timestamp: Date.now(),
    });

    return promise;
  }

  /**
   * Makes a POST request
   * 
   * @param endpoint - API endpoint
   * @param body - Request body
   * @param options - Request options
   * @param options.invalidateCache - Array of cache keys to invalidate after POST
   * @returns Promise resolving to the response data
   * 
   * @example
   * ```ts
   * await apiClient.post("/user/123/progress", { task_id: 1, status: "success" }, {
   *   invalidateCache: ["GET:/user/123"],
   * });
   * ```
   */
  async post<T>(endpoint: string, body: any, options?: {
    invalidateCache?: string[]; // Cache keys to invalidate
  }): Promise<T> {
    const response = await fetch(`${API_HOST}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    const data = await response.json();

    // Invalidate specified cache entries
    if (options?.invalidateCache) {
      options.invalidateCache.forEach(key => {
        this.cache.delete(key);
        this.pendingRequests.delete(key);
      });
    }

    return data;
  }

  /**
   * Invalidates cache for specific endpoint
   * 
   * @param endpoint - API endpoint to invalidate (e.g., "/user/123")
   * 
   * @example
   * ```ts
   * apiClient.invalidate("/user/123");
   * ```
   */
  invalidate(endpoint: string): void {
    const cacheKey = `GET:${endpoint}`;
    this.cache.delete(cacheKey);
    this.pendingRequests.delete(cacheKey);
  }

  /**
   * Clears all cache and pending requests
   * 
   * @example
   * ```ts
   * apiClient.clearCache();
   * ```
   */
  clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }
}

// Singleton instance
export const apiClient = new ApiClient();

